/**
 * useNotesSlashCommand — shared inline-slash controller for Notes editors.
 *
 * Opens the SlashMenu component when the user types `/` at the start of
 * an empty block. onInput returns `true` if the event was intercepted so
 * the caller short-circuits its own inline-editor handlers.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { SlashMenu, type BlockType } from '@/components/requirements/SlashMenu'
import { placeCaretAtEnd } from './useAutoFocus'

interface Options {
  /** The block's current committed value (title or body). Slash commands are
   *  only active when the block is empty, so existing content's "/" stays literal. */
  value: string
  onPick: (type: BlockType) => Promise<void> | void
}

interface SlashCommandAPI {
  isOpen: boolean
  onInput: (e: React.FormEvent<HTMLElement>) => boolean
  onBlur: (e: React.FocusEvent<HTMLElement>) => boolean
  menu: ReactNode
}

export function useNotesSlashCommand({ value, onPick }: Options): SlashCommandAPI {
  const [slashOpen, setSlashOpen] = useState<{ top: number; left: number } | null>(null)
  const [slashFilter, setSlashFilter] = useState('')
  const targetRef = useRef<HTMLElement | null>(null)

  const clearCommandText = useCallback(() => {
    const el = targetRef.current
    if (!el) return
    if ((el.textContent ?? '').startsWith('/')) {
      el.textContent = ''
      placeCaretAtEnd(el)
    }
  }, [])

  const closeAndClear = useCallback(() => {
    clearCommandText()
    setSlashOpen(null)
    setSlashFilter('')
  }, [clearCommandText])

  const onInput = useCallback((e: React.FormEvent<HTMLElement>) => {
    const el = e.currentTarget
    const text = el.textContent ?? ''
    if (value.trim() === '' && text.startsWith('/')) {
      const rect = el.getBoundingClientRect()
      targetRef.current = el
      setSlashOpen({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX })
      setSlashFilter(text)
      return true
    }
    if (slashOpen) {
      setSlashOpen(null)
      setSlashFilter('')
    }
    return false
  }, [slashOpen, value])

  const onBlur = useCallback((_e: React.FocusEvent<HTMLElement>) => {
    if (!slashOpen) return false
    window.setTimeout(closeAndClear, 120)
    return true
  }, [closeAndClear, slashOpen])

  const handlePick = useCallback((type: BlockType) => {
    clearCommandText()
    setSlashOpen(null)
    setSlashFilter('')
    void onPick(type)
  }, [clearCommandText, onPick])

  const menu = slashOpen
    ? <SlashMenu anchor={slashOpen} filter={slashFilter} onPick={handlePick} onClose={closeAndClear} />
    : null

  return { isOpen: slashOpen !== null, onInput, onBlur, menu }
}
